import { useState } from 'react';
import type { PasswordLinkResult } from '../api';
import { PASSWORD_LINK_PURPOSE } from '../admin/labels';
import { fmtDateTime } from '../admin/format';

/**
 * 비밀번호 설정 링크 발급 결과 안내(§14.19.9) — 사용자 추가 · 초대 다시 보내기 · 재설정 메일 · 수퍼유저 새 조직이 함께 쓴다.
 *
 * - 메일이 나갔으면: "○○ 에게 초대 메일을 보냈습니다(기한)".
 * - 메일이 안 나갔으면(SMTP 미설정·실패 — 사용자 결정 "관리자 화면에 링크 표시"): 링크를 보여 주고 [복사] —
 *   관리자가 메신저 등으로 **본인에게만** 전달한다. 링크는 한 번 쓰면 끝이고, 새로 보내면 이 링크는 무효가 된다.
 */
export function PasswordLinkNotice({
  who,
  email,
  result,
  onClose,
}: {
  /** 받는 사람 이름(문장에 쓴다). */
  who: string;
  email: string;
  result: PasswordLinkResult;
  onClose?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const what = result.purpose === PASSWORD_LINK_PURPOSE.INVITE ? '초대' : '비밀번호 재설정';
  const until = `${fmtDateTime(result.expiresAt)} 까지 한 번 쓸 수 있습니다.`;

  const copy = async () => {
    if (!result.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      // 클립보드가 막힌 환경 — 칸을 선택해 두면 사람이 Ctrl+C 로 복사할 수 있다.
      (document.getElementById('pw-link-field') as HTMLInputElement | null)?.select();
    }
  };

  return (
    <div className={`pw-link-notice ${result.sent ? '' : 'warn'}`} role="status">
      {result.sent ? (
        <div>
          <b>{who}</b>({email}) 에게 {what} 메일을 보냈습니다. {until}
          {/* 2026-09-28 실측: 새 Gmail 발송 계정의 첫 메일이 받는 쪽 스팸함으로 갔다 — 받는 사람에게 미리 알려 주게. */}
          <br />
          메일이 안 보이면 <b>스팸함</b>을 확인하라고 전해 주세요.
        </div>
      ) : (
        <>
          <div>
            ⚠️ {what} 메일을 보내지 못했습니다. 아래 링크를 <b>{who}</b>({email}) 본인에게만 직접 전달하세요. {until}
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <input
              id="pw-link-field"
              readOnly
              value={result.link ?? ''}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="비밀번호 설정 링크"
              style={{ flex: 1, minWidth: 0 }}
            />
            <button onClick={() => void copy()}>{copied ? '복사됨' : '복사'}</button>
          </div>
        </>
      )}
      {onClose && (
        <button className="linklike" style={{ marginTop: 6 }} onClick={onClose}>
          닫기
        </button>
      )}
    </div>
  );
}
